/**
 * WebAuthn base64url encoding helpers.
 *
 * The Web Authentication API works with ArrayBuffers, but storage and
 * transport require base64url strings (RFC 4648 Section 5). These
 * helpers convert between the two representations.
 *
 * All functions are pure and synchronous — no side effects, no DOM
 * dependencies beyond TextEncoder/TextDecoder (available in all
 * modern runtimes including Deno Edge Functions).
 */

/**
 * Encodes a Uint8Array to a base64url string (no padding).
 */
export function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Decodes a base64url string (with or without padding) to a Uint8Array.
 */
export function base64UrlToUint8Array(base64url: string): Uint8Array {
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Converts an ArrayBuffer to a base64url string.
 */
export function bufferToBase64Url(buffer: ArrayBuffer): string {
  return uint8ArrayToBase64Url(new Uint8Array(buffer))
}

/**
 * Converts a base64url string to an ArrayBuffer.
 */
export function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const arr = base64UrlToUint8Array(base64url)
  return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer
}
